import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { UpdatePropertyCommand } from '../../core/command/update-property.command.js';
import { createTextBoxDefaults } from './textbox-defaults.factory.js';
import { isTextBox } from './textbox.types.js';
import type { TextboxEditorController } from './textbox-editor.controller.js';
import type { TextDocument, TextDocumentNode } from './text-document.type.js';
import { countVisibleTaskItems } from './text-document-migration.js';

/**
 * Optional bridge to the active `TextboxEditorController` so format and
 * font-size UiActions can be delegated into the live contentEditable
 * overlay. The element wires this when both controllers are constructed
 * (T16 follow-up); tests pass `null` and assert no delegation occurs.
 */
export interface TextboxEditorBridge {
    applyFormat: TextboxEditorController['applyFormat'];
    applyFontSize: TextboxEditorController['applyFontSize'];
    // O2D.W-PRD-035 §TR1 — delegate a list-button click into the live editor's
    // selection-scoped `toggle*List` command while editing.
    applyListType: TextboxEditorController['applyListType'];
    // Per-paragraph horizontal alignment — delegate an align-button click into
    // the live editor's selection-scoped `setTextAlign` while editing.
    applyTextAlign: TextboxEditorController['applyTextAlign'];
}

/**
 * Slice-owned subscriber for textbox lifecycle UiActions (PRD-F6 §TR8):
 *
 *  1. `place/textbox` — derive defaults from the placement rect, push an
 *     `AddObjectCommand`, then re-emit `selection/set` and
 *     `edit/enter-textbox` so the new textbox is selected and immediately
 *     editable (mirroring the legacy `placeTextAt` flow).
 *  2. `textbox/toggle-checklist-item` — flip the `[ ] ` / `[x] ` marker on
 *     the Nth visible (non-empty) line and push an `UpdatePropertyCommand`.
 *  3. `textbox/apply-format` — delegate selection formatting into the
 *     active editor (no-op when no editor is open).
 *  4. `textbox/apply-fontsize` — delegate world-unit font-size into the
 *     active editor (no-op when no editor is open).
 *  5. `textbox/fit-to-content` — grow the textbox height to the measured
 *     `requiredHeight` via an `UpdatePropertyCommand` so the auto-expand is
 *     undoable; never mutates `BoardState` directly (PRD-RTE-OVERFLOW §TR7).
 *
 * Lifecycle: all subscriptions use `takeUntil(bus.destroy$)` (FC11). The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 */
export class TextboxLifecycleController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #editor: TextboxEditorBridge | null;

    constructor(
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
        editor: TextboxEditorBridge | null = null,
    ) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
        this.#editor = editor;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/textbox' }> =>
                        a.type === 'place/textbox',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] place/textbox bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/toggle-checklist-item' }> =>
                        a.type === 'textbox/toggle-checklist-item',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handleToggle(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] toggle-checklist-item bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/apply-format' }> =>
                        a.type === 'textbox/apply-format',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => {
                    this.#editor?.applyFormat(action.formatKey, action.value);
                },
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] apply-format bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/apply-fontsize' }> =>
                        a.type === 'textbox/apply-fontsize',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => {
                    this.#editor?.applyFontSize(action.wu, action.scale);
                },
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] apply-fontsize bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/apply-list' }> =>
                        a.type === 'textbox/apply-list',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => {
                    this.#editor?.applyListType(action.listType);
                },
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] apply-list bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/apply-align' }> =>
                        a.type === 'textbox/apply-align',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => {
                    this.#editor?.applyTextAlign(action.align);
                },
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] apply-align bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'textbox/fit-to-content' }> =>
                        a.type === 'textbox/fit-to-content',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handleFitToContent(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxLifecycleController] fit-to-content bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/textbox' }>): void {
        const { x, y, width, height } = action.rect;
        const scale = this.#bus.viewport$.value.scale || 1;
        // Stefan, 2026-09-03 — "choose properties before drawing": a value
        // staged in the options circle while the tool was armed (see
        // `stagePrePlacementProperty()` in `o2d-svg-optionsbar.element.ts`),
        // if any. Absent → the factory's own hardcoded defaults apply, exactly
        // as before this feature.
        const staged = this.#bus.stagedToolProperties$.value.text;
        const obj = createTextBoxDefaults(scale, { x, y }, { width, height }, staged);

        this.#history.push(new AddObjectCommand(obj), this.#state);

        const newId = objectId(obj.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
        this.#bus.uiActions$.next({ type: 'edit/enter-textbox', id: newId });
    }

    #handleToggle(action: Extract<UiAction, { type: 'textbox/toggle-checklist-item' }>): void {
        const obj = this.#state.getObject(action.id);
        if (!obj || !isTextBox(obj)) return;
        if (!obj.document) return;
        // NOT `obj.listType !== 'checklist'`: same reasoning as the renderer's
        // hotspot gate (textbox.renderer.ts, rebuildChecklistHotspots) — that
        // advisory scalar collapses to 'none' for a MIXED document (e.g. a
        // plain first line + a checklist second line), which would reject a
        // toggle that reached here from a perfectly real checklist item.
        if (countVisibleTaskItems(obj.document) === 0) return;

        const next = toggleChecklistInDocument(obj.document, action.lineIndex);
        if (next === obj.document) return;

        this.#history.push(new UpdatePropertyCommand(action.id, 'document', next), this.#state);
    }

    // Grow the textbox to fit its content by pushing an undoable height change.
    // The command is the single writer; BoardState is never mutated directly
    // (Command-Bus discipline, PRD-RTE-OVERFLOW §TR7).
    #handleFitToContent(action: Extract<UiAction, { type: 'textbox/fit-to-content' }>): void {
        const obj = this.#state.getObject(action.id);
        if (!obj || !isTextBox(obj)) return;
        if (action.requiredHeight <= obj.height) return;

        this.#history.push(
            new UpdatePropertyCommand(action.id, 'height', action.requiredHeight),
            this.#state,
        );
    }
}

// True when a taskItem's content carries at least one non-whitespace text run.
// Mirrors the renderer's hit-test which counts only non-empty visible lines.
function taskItemHasVisibleContent(item: TextDocumentNode): boolean {
    let buf = '';
    const walk = (n: TextDocumentNode): void => {
        if (typeof n.text === 'string') buf += n.text;
        if (n.content) for (const c of n.content) walk(c);
    };
    walk(item);
    return buf.trim().length > 0;
}

/**
 * Flip the `checked` attribute on the Nth visible (non-empty) taskItem inside
 * the first `taskList` node of the document. Returns a new document with
 * structural sharing when nothing matches (callers compare by reference to
 * detect no-op).
 *
 * The `visibleIndex` matches the index the renderer's checklist hotspots are
 * tagged with (`o2dChecklistIndex`, `rebuildChecklistHotspots` in
 * textbox.renderer.ts), fed by `measureChecklistLines`
 * (textbox-svg-renderer.ts) — which likewise skips empty `<li>` items; we
 * apply the same skip rule (`taskItemHasVisibleContent`) when walking
 * taskItems here, so both sides agree on what "the Nth visible item" means.
 */
function toggleChecklistInDocument(doc: TextDocument, visibleIndex: number): TextDocument {
    if (visibleIndex < 0) return doc;
    const content = doc.content ?? [];
    const listNodeIndex = content.findIndex((n) => n.type === 'taskList');
    if (listNodeIndex === -1) return doc;

    const listNode = content[listNodeIndex]!;
    const items = listNode.content ?? [];
    let visible = -1;
    let targetIndex = -1;
    for (let i = 0; i < items.length; i++) {
        if (!taskItemHasVisibleContent(items[i]!)) continue;
        visible++;
        if (visible === visibleIndex) {
            targetIndex = i;
            break;
        }
    }
    if (targetIndex === -1) return doc;

    const originalItem = items[targetIndex]!;
    const wasChecked = (originalItem.attrs as { checked?: unknown } | undefined)?.checked === true;
    const toggledItem: TextDocumentNode = {
        ...originalItem,
        attrs: { ...(originalItem.attrs ?? {}), checked: !wasChecked },
    };
    const nextItems = items.slice();
    nextItems[targetIndex] = toggledItem;
    const nextList: TextDocumentNode = { ...listNode, content: nextItems };
    const nextContent = content.slice();
    nextContent[listNodeIndex] = nextList;
    return { ...doc, content: nextContent };
}
